import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { BelegAbschlaege, BelegAnsicht, BelegFeld } from './belegAnsichtTypes';
import { BelegKontext, feldPfadZuRequest } from '../lib/belegFelder';
import { bezugZeile, summenTeilen } from '../lib/rechnungsart';
import './BelegDokumentView.css';

interface Props {
  ansichtUrl: string;          // vollstaendige URL inkl. ?typ=
  kontext: BelegKontext;
  onKonflikt?: () => void;     // 409: Aufrufer laedt seine Maske neu
}

const eur = (s?: string | null) =>
  s == null ? '-' : `${Number(s).toLocaleString('de-DE', {
    minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;

const datumDe = (iso?: string | null) => {
  if (!iso) return '';
  const t = iso.slice(0, 10).split('-');
  return t.length === 3 ? `${t[2]}.${t[1]}.${t[0]}` : iso;
};

/** Klick -> Input im Dokumentfluss; onBlur/Enter persistiert. */
const InlineFeld: React.FC<{
  feld?: BelegFeld; anzeige: React.ReactNode; mehrzeilig?: boolean;
  onSave: (feldPfad: string, wert: string) => Promise<void>;
}> = ({ feld, anzeige, mehrzeilig, onSave }) => {
  const [editing, setEditing] = useState(false);
  const [wert, setWert] = useState('');
  if (!feld) return <>{anzeige}</>;
  if (!editing) {
    return (
      <span className="beleg-edit" title="Klicken zum Bearbeiten"
            onClick={() => { setWert(feld.wert); setEditing(true); }}>
        {anzeige}
      </span>
    );
  }
  const speichern = async () => {
    setEditing(false);
    // Dirty-Check: unveraenderter Wert -> kein PUT, kein Audit-Eintrag.
    if (wert === feld.wert) return;
    await onSave(feld.feld_pfad, wert);
  };
  const abbrechen = () => setEditing(false);
  const tasten = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') abbrechen();
    if (e.key === 'Enter' && !mehrzeilig) { e.preventDefault(); speichern(); }
  };
  return mehrzeilig ? (
    <textarea autoFocus className="beleg-edit__input beleg-edit__input--area"
              value={wert} onChange={e => setWert(e.target.value)}
              onBlur={speichern} onKeyDown={tasten} />
  ) : (
    <input autoFocus className="beleg-edit__input"
           value={wert} onChange={e => setWert(e.target.value)}
           onBlur={speichern} onKeyDown={tasten} />
  );
};

/** Tabelle der verrechneten Abschlagsrechnungen einer Schlussrechnung (§ 14 Abs. 5 UStG). */
const AbschlagTabelle: React.FC<{ abschlaege: BelegAbschlaege }> = ({ abschlaege }) => (
  <table className="k-doc__pos beleg-doc__abschlaege">
    <caption>{abschlaege.ueberschrift}</caption>
    <thead><tr>
      <th>Rechnung</th><th>Datum</th>
      <th className="is-num">Netto</th><th className="is-num">USt</th><th className="is-num">Brutto</th>
    </tr></thead>
    <tbody>
      {abschlaege.zeilen.map((z, i) => (
        <tr key={i}>
          <td>{z.nummer}</td><td>{z.datum}</td>
          <td className="is-num">{eur(z.netto)}</td>
          <td className="is-num">{eur(z.mwst)}</td>
          <td className="is-num">{eur(z.brutto)}</td>
        </tr>
      ))}
    </tbody>
    <tfoot><tr>
      <td colSpan={2}>Summe</td>
      <td className="is-num">{eur(abschlaege.summe.netto)}</td>
      <td className="is-num">{eur(abschlaege.summe.mwst)}</td>
      <td className="is-num">{eur(abschlaege.summe.brutto)}</td>
    </tr></tfoot>
  </table>
);

const BelegDokumentView: React.FC<Props> = ({ ansichtUrl, kontext, onKonflikt }) => {
  const [ansicht, setAnsicht] = useState<BelegAnsicht | null>(null);
  const [fehler, setFehler] = useState('');

  // Ohne schreibbaren Kontext (z.B. kontext={} aus der Akte) gibt es keinen
  // Schreibpfad — dann darf auch keine Editier-Affordance erscheinen.
  const editierbar = kontext.auftragId != null || kontext.bestellungId != null;
  const feldWenn = (f?: BelegFeld) => (editierbar ? f : undefined);

  const laden = useCallback(async () => {
    try {
      const res = await axios.get<BelegAnsicht>(ansichtUrl);
      setAnsicht(res.data);
      setFehler('');
    } catch (err: any) {
      setFehler(err?.response?.data?.detail || 'Beleg-Ansicht konnte nicht geladen werden.');
    }
  }, [ansichtUrl]);

  useEffect(() => { laden(); }, [laden]);

  const speichern = async (feldPfad: string, wert: string) => {
    const req = feldPfadZuRequest(kontext, feldPfad, wert);
    if (!req) return;
    try {
      await axios.put(req.url, req.body);
    } catch (err: any) {
      if (err?.response?.status === 409) { onKonflikt?.(); }
      setFehler(err?.response?.data?.detail || 'Speichern fehlgeschlagen.');
    }
    // Immer neu laden: Summen/Zuschlaege/Fussnoten zieht der Server nach.
    await laden();
  };

  if (fehler && !ansicht) return <div className="hinweis-box">{fehler}</div>;
  if (!ansicht) return <div className="beleg-doc__laden">Lade Beleg…</div>;

  const { beleg, layout } = ansicht;
  const spalten = layout.mit_preisen ? 6 : 4;
  // Gutschrift/Storno: Bezugszeile statt der (dann doppelten) Meta-Zeile „Bezug“.
  const bezugLinie = bezugZeile(beleg.rechnungsart, beleg.bezug);
  const metaZeilen = bezugLinie
    ? ansicht.meta_zeilen.filter(m => m.label !== 'Bezug')
    : ansicht.meta_zeilen;
  const summen = summenTeilen(ansicht.summen.zeilen, !!ansicht.abschlaege);

  return (
    <div className="k-doc-wrap">
      {beleg.ist_entwurf && <div className="k-doc__draft">Entwurf</div>}
      {fehler && <div className="hinweis-box">{fehler}</div>}
      {beleg.ist_entwurf && (ansicht.absender.pflicht_maengel?.length ?? 0) > 0 && (
        <div className="hinweis-box">
          <strong>Pflichtangaben fehlen – vor dem Versand in Einstellungen → Firma ergänzen:</strong>
          <ul>{ansicht.absender.pflicht_maengel!.map((m, i) => <li key={i}>{m}</li>)}</ul>
        </div>
      )}
      <div className="k-doc">
        <div className="k-doc__head">
          <span className="k-doc__typ">{beleg.titel_text}</span>
          <span className="k-doc__nr">
            {[beleg.nummer, datumDe(beleg.datum)].filter(Boolean).join(' · ')}
          </span>
        </div>

        <div className="beleg-doc__absenderzeile">
          {[ansicht.absender.name, ...ansicht.absender.zeilen].join(' · ')}
        </div>
        <div className="k-doc__addr">
          {ansicht.empfaenger.zeilen.map((z, i) =>
            i === 0 ? <strong key={i}>{z}</strong> : <span key={i}>{z}<br /></span>)}
        </div>

        {bezugLinie && <p className="beleg-doc__text beleg-doc__bezug">{bezugLinie}</p>}

        {(ansicht.kommission.text || (editierbar && ansicht.kommission.feld_pfad)) && (
          <p className="beleg-doc__text beleg-doc__kommission">
            <InlineFeld feld={feldWenn(ansicht.kommission.feld_pfad
                ? { feld_pfad: ansicht.kommission.feld_pfad, wert: ansicht.kommission.text } : undefined)}
              anzeige={ansicht.kommission.text || '+ Kommission'} onSave={speichern} />
          </p>
        )}

        {metaZeilen.length > 0 && (
          <table className="k-doc__meta"><tbody>
            {metaZeilen.map((m, i) => (
              <tr key={i}><td>{m.label}</td><td>{m.wert}</td></tr>
            ))}
          </tbody></table>
        )}

        {ansicht.intro_texte.map((t, i) => <p key={i} className="beleg-doc__text">{t}</p>)}

        <p className="beleg-doc__text beleg-doc__text--frei">
          <InlineFeld feld={feldWenn(ansicht.kopftext.feld_pfad
              ? { feld_pfad: ansicht.kopftext.feld_pfad, wert: ansicht.kopftext.text } : undefined)}
            anzeige={ansicht.kopftext.text
              || (editierbar && ansicht.kopftext.feld_pfad ? '+ Kopftext' : '')}
            mehrzeilig onSave={speichern} />
        </p>

        <table className="k-doc__pos">
          <thead><tr>
            <th>Pos.</th><th>Bezeichnung</th><th className="is-num">Menge</th><th>Einheit</th>
            {layout.mit_preisen && <th className="is-num">Einzelpreis</th>}
            {layout.mit_preisen && <th className="is-num">Gesamt</th>}
          </tr></thead>
          <tbody>
            {ansicht.titel.flatMap((titel, tIdx) => titel.positionen.map(pos => (
              <React.Fragment key={`${tIdx}-${pos.nummer}`}>
                <tr>
                  <td>{pos.nummer}</td>
                  <td>
                    <InlineFeld feld={feldWenn(pos.felder?.bezeichnung)} mehrzeilig
                      anzeige={pos.bezeichnung} onSave={speichern} />
                  </td>
                  <td className="is-num">
                    <InlineFeld feld={feldWenn(pos.felder?.menge)}
                      anzeige={pos.menge} onSave={speichern} />
                  </td>
                  <td>{pos.einheit}</td>
                  {layout.mit_preisen && (
                    <td className="is-num">
                      <InlineFeld feld={feldWenn(pos.felder?.einzelpreis)}
                        anzeige={eur(pos.einzelpreis)} onSave={speichern} />
                    </td>
                  )}
                  {layout.mit_preisen && <td className="is-num">{eur(pos.gesamtpreis)}</td>}
                </tr>
                {pos.zusatzzeilen.map((z, i) => (
                  <tr key={`${pos.nummer}-z${i}`} className="beleg-doc__zusatzzeile">
                    <td></td><td colSpan={spalten - 1}>{z}</td>
                  </tr>
                ))}
              </React.Fragment>
            )))}
          </tbody>
        </table>

        {layout.mit_summen && (
          <>
            {[summen.vor, summen.nach].map((zeilen, teil) => zeilen.length > 0 && (
              <React.Fragment key={teil}>
                {teil === 1 && ansicht.abschlaege && <AbschlagTabelle abschlaege={ansicht.abschlaege} />}
                <table className="k-doc__sum"><tbody>
                  {zeilen.map((z, i) => (
                    <tr key={i}>
                      <td>
                        {z.feld_pfad && editierbar ? (
                          <InlineFeld feld={feldWenn({ feld_pfad: z.feld_pfad, wert: z.wert || '' })}
                            anzeige={z.label} onSave={speichern} />
                        ) : (
                          <>{z.label}{z.basis && ansicht.summen.zeilen.filter(s => s.art === 'steuer').length > 1
                            ? ` (aus ${eur(z.basis)})` : ''}</>
                        )}
                      </td>
                      <td>{eur(z.betrag)}</td>
                    </tr>
                  ))}
                </tbody></table>
              </React.Fragment>
            ))}
          </>
        )}

        {(ansicht.zahlungsbedingungen.text || ansicht.zahlungsbedingungen.felder) && (
          <p className="beleg-doc__text">
            {ansicht.zahlungsbedingungen.felder ? (
              <>
                Zahlungsziel:{' '}
                <InlineFeld feld={feldWenn(ansicht.zahlungsbedingungen.felder.zahlungsziel_tage)}
                  anzeige={ansicht.zahlungsbedingungen.felder.zahlungsziel_tage.wert || '—'}
                  onSave={speichern} /> Tage netto, Skonto{' '}
                <InlineFeld feld={feldWenn(ansicht.zahlungsbedingungen.felder.skonto_prozent)}
                  anzeige={ansicht.zahlungsbedingungen.felder.skonto_prozent.wert || '—'}
                  onSave={speichern} /> % innerhalb{' '}
                <InlineFeld feld={feldWenn(ansicht.zahlungsbedingungen.felder.skonto_frist_tage)}
                  anzeige={ansicht.zahlungsbedingungen.felder.skonto_frist_tage.wert || '—'}
                  onSave={speichern} /> Tagen
              </>
            ) : ansicht.zahlungsbedingungen.text}
          </p>
        )}

        {ansicht.hinweise.map((h, i) => <p key={i} className="beleg-doc__text">{h}</p>)}

        <p className="beleg-doc__text beleg-doc__text--frei">
          <InlineFeld feld={feldWenn(ansicht.fusstext.feld_pfad
              ? { feld_pfad: ansicht.fusstext.feld_pfad, wert: ansicht.fusstext.text } : undefined)}
            anzeige={ansicht.fusstext.text
              || (editierbar && ansicht.fusstext.feld_pfad ? '+ Fußtext' : '')}
            mehrzeilig onSave={speichern} />
        </p>

        <div className="beleg-doc__fussnoten">
          {ansicht.fussnoten.map((f, i) => <div key={i}>{f}</div>)}
          {ansicht.absender.bank.iban && (
            <div>
              Bankverbindung: {ansicht.absender.bank.bank} · IBAN: {ansicht.absender.bank.iban}
              {ansicht.absender.bank.bic ? ` · BIC: ${ansicht.absender.bank.bic}` : ''}
            </div>
          )}
          {ansicht.absender.pflichtangaben && <div>{ansicht.absender.pflichtangaben}</div>}
        </div>
      </div>
    </div>
  );
};

export default BelegDokumentView;
